import { useEffect, useState } from "react";

import CleanupColl from "../../firebase/CleanupsColl";
import EventColl from "../../firebase/EventsColl";
import UsersColl from "../../firebase/UsersColl";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import LoadingSpinner from "../../components/LoadingSpinner";

import { ORG_NAME, ORG_ID } from "../../util/CommonConstants";
import UtilDate from "../../util/UtilDate";
import "../../static/css/AdminScreen.css";

export const AdminScreen = () => {
  const [organizationName, setOrganizationName] = useLocalStorage(ORG_NAME, "");
  const [organizationId, setOrganizationId] = useLocalStorage(ORG_ID, "");

  const [unapprovedCleanups, setUnapprovedCleanups] = useState([]);
  const [unapprovedEvents, setUnapprovedEvents] = useState([]);
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  const fetchData = async () => {
    try {
      setIsLoading(true);

      let cleanups = await CleanupColl.shared.getUnapprovedCleanups();
      setUnapprovedCleanups(cleanups);

      let events = await EventColl.shared.getUnapprovedEvents();
      setUnapprovedEvents(events);

      let users = await UsersColl.shared.getAllUsers(organizationId);
      setUsers(users);
    } catch (error) {
      console.error("Error fetching admin data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Refetch users when organizationId changes
  useEffect(() => {
    if (organizationId) {
      setIsLoading(true);
      UsersColl.shared
        .getAllUsers(organizationId)
        .then(setUsers)
        .catch((error) => console.error("Error fetching users:", error))
        .finally(() => setIsLoading(false));
    }
  }, [organizationId]);

  // Filter users based on search term
  const filteredUsers = users.filter(
    (user) =>
      user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (isLoading) {
    return (
      <div>
        <h1>{organizationName} Admin Center</h1>
        <LoadingSpinner
          size="large"
          text="Loading admin data..."
          color="#007bff"
        />
      </div>
    );
  }

  return (
    <div>
      <h1>{organizationName} Admin Center</h1>

      <div className="approval-container">
        <h3>Approvals</h3>
        <div className="approval-card">
          <h2>Cleanups</h2>
          <h3>{unapprovedCleanups ? unapprovedCleanups.length : 0}</h3>
          <p>Awaiting Approval</p>

          {unapprovedCleanups && unapprovedCleanups.length > 0 ? (
            <a href="#">Approve Cleanups (coming soon)</a>
          ) : null}
        </div>

        <div className="approval-card">
          <h2>Events</h2>
          <h3>{unapprovedEvents ? unapprovedEvents.length : 0}</h3>
          <p>Awaiting Approval</p>

          {unapprovedEvents && unapprovedEvents.length > 0 ? (
            <a href="/admin/event/approvals">Approve Events</a>
          ) : null}
        </div>
      </div>

      <div className="users-container">
        <div className="users-header">
          <h3>Users ({filteredUsers.length})</h3>
          <div className="search-container">
            <input
              type="text"
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
        </div>

        <div className="users-table-container">
          <table className="users-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Last Login</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.length > 0 ? (
                filteredUsers.map((user) => (
                  <tr key={user.id} className="user-row">
                    <td className="user-name">
                      <div className="user-avatar">
                        {user.first_name?.charAt(0)?.toUpperCase()}
                      </div>
                      <span>
                        {user.first_name + " " + user.last_name || "N/A"}
                      </span>
                    </td>
                    <td className="user-email">{user.email || "N/A"}</td>
                    <td className="user-role">
                      <span className="role-badge">{user.role || "User"}</span>
                    </td>
                    <td className="user-name">
                      <span>
                        {UtilDate.getDateTimeStringLong(user.last_app_open) ||
                          "Never"}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="no-users">
                    {searchTerm
                      ? "No users found matching your search."
                      : "No users found."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
